import { UserTypeEnum, UserDetail } from '../../../js/api';
import { resolveRoleHome } from './roleHomeRoutes';

/**
 * Determines the home URL for a user based on their user type and current location.
 * This centralizes the logic for where users should be redirected when clicking on logos.
 *
 * @param user - The current user object (can be undefined if not logged in)
 * @param pathname - The current pathname from the location (e.g., from useLocation hook)
 * @returns The home URL string for the user
 */
export function getHomeUrl(user: UserDetail | undefined, pathname: string): string {
  const isAppPage =
    pathname.startsWith('/app/healthcare-entity') ||
    pathname.startsWith('/app/organization') ||
    pathname.startsWith('/app/provider');

  if (user?.user_type === UserTypeEnum.PROVIDER) {
    return '/app/provider/home';
  } else if (user?.user_type === UserTypeEnum.HEALTHCARE_ENTITY_REP) {
    return resolveRoleHome(user.healthcare_entity_rep_role, '/app/healthcare-entity');
  } else if (user?.user_type === UserTypeEnum.STAFF) {
    return '/staff/home';
  } else {
    return 'https://www.senditgigs.com/';
  }
}

/**
 * Router state a caller attaches when it links into another page so that page
 * can offer a way back. `referrer` is the caller's full URL (path + query, so
 * an open drawer and its filters come back too); `referrerLabel` names the
 * caller for the back link (e.g. "Providers" renders "← Providers").
 *
 * Full-page detail routes already read `referrer` for their close button; the
 * list pages also render `referrerLabel` via `ReferrerBackLink`.
 */
export type ReferrerState = {
  referrer?: string;
  referrerLabel?: string;
};

/** Build the router state for a link whose destination should offer "← label" back here. */
export function referrerState(
  location: { pathname: string; search: string },
  referrerLabel?: string
): ReferrerState {
  return {
    referrer: location.pathname + location.search,
    ...(referrerLabel ? { referrerLabel } : {}),
  };
}

/** Staff full-page provider view (the provider drawer's popout + the stethoscope links). */
export const staffProviderDetailPath = (providerId: string, tab?: string | null) => {
  const tabParam = tab ? `?tab=${tab}` : '';
  return `/staff/providers/${providerId}${tabParam}`;
};

/** Org (HCO) provider profile page — legacy; what org users' "View Profile" opened. */
export const orgProviderProfilePath = (providerId: string) =>
  `/app/healthcare-entity/providers/${providerId}`;
